/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
// Ember Imports
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty, typeOf } from '@ember/utils';
import { cancel, later } from '@ember/runloop';

// External Imports
import moment from 'moment';

// Internal Imports
import EventStatus from '@linc-technologies/linced-common/enums/events/event-status';
import EventType from '@linc-technologies/linced-common/enums/events/event-type';
import PermissionType from '@linc-technologies/linced-common/enums/events/permission-type';
import SharingLevel from '@linc-technologies/linced-common/enums/media-sharing-level';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  getKeyAndSourceFromUpload,
  onAttachmentUpload,
} from '@linc-technologies/linced-common/utils/uppy';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import {
  autosaveCreateEditorId,
  autosaveTinyMCEConfig,
  manageAutosaveDraftsButton,
  removeAutosavedDrafts,
} from '@linc-technologies/linced-common/utils/tinymce';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import {
  filterBy,
  findBy,
  mapBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';
import { observes } from '@ember-decorators/object';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class NewEvent extends Component {
  // Services
  @service
  features;

  @service
  googleDrive;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  @service
  uppy;

  EventStatus = EventStatus;
  EventType = EventType;
  InputType = InputType;
  Jurisdiction = Jurisdiction;
  PermissionType = PermissionType;
  Scopes = Scopes;
  @tracked
  ledgerItems = [];
  @tracked
  ledgerItemsInUse = [];
  @tracked
  descriptionViolatingOptions = [];
  @tracked
  noteViolatingOptions = [];

  @computed('descriptionViolatingOptions.[]', 'noteViolatingOptions.[]')
  get hasLanguageViolations() {
    return (
      !isEmpty(this.descriptionViolatingOptions) ||
      !isEmpty(this.noteViolatingOptions)
    );
  }

  // calendarMode indicates that we're creating an event from the calendar
  // Treat the events slightly differently
  calendarMode = false;

  @tracked
  currentStep = 0;
  dialogOpen = false;
  @tracked
  saveInProgress = false;
  selectedDate = null;
  @tracked
  cancelling = false;
  sortingFieldPermissions = false;
  sortingOptions = false;
  sortingPermissions = false;
  @tracked
  attachments = [];
  @tracked
  showCloseAt = false;
  @tracked
  uploading = false;
  @tracked
  hideImage = false;
  @tracked
  empty = true;
  @optionAlias('finance:settings:caregiverAccessEnabled')
  financeCaregiverAccessEnabled;
  @optionAlias('finance:payments:enabled')
  paymentsEnabled;
  @tracked
  stripeConnected = false;
  @tracked
  locations = [];
  @tracked
  templates = [];
  // The selects get records, not the async belongsTo proxies of the event.
  // WarpDrive gives the same proxy object after each change, and
  // ember-power-select waits for a promise in @selected only one time per
  // object, so a proxy never shows a new pick (HE-288).
  @tracked
  selectedLocation = null;
  @tracked
  selectedTemplate = null;
  @tracked
  risks = [];
  refresh() {}

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('session.school')
  get computedSchool() {
    return this.get('session.school');
  }

  refreshLocations() {
    // schools/location is fetched via store.query, which returns a
    // PromiseArray -- accessing .length on it directly (as the template
    // does) is deprecated, so resolve it into a plain tracked array first.
    this.store
      .query('schools/location', {
        schoolId: this.session.activeSchool,
      })
      .then((locations) => {
        if (this.isDestroying || this.isDestroyed) {
          return;
        }
        this.locations = locations.slice();
      });
  }

  refreshTemplates() {
    // events/template is fetched via store.query, which returns a
    // PromiseArray -- handing it straight to PaperSelect's @options is
    // deprecated (internals like isArray()/toPlainArray() touch it), so
    // resolve it into a plain tracked array first.
    this.store
      .query('events/template', {
        schoolId: this.session.activeSchool,
      })
      .then((templates) => {
        if (this.isDestroying || this.isDestroyed) {
          return;
        }
        this.templates = templates.slice();
      });
  }

  @computed('currentUser.activeSchool')
  get permissionFields() {
    return this.store
      .peekAll('people-schema/field')
      .filter((field) => field.studentPermission);
  }

  @computed('session.{activeSchool,personId}')
  get filteredGroups() {
    let currentYear = moment().year();
    return this.store
      .peekAll('group')
      .filter((group) => {
        // A year of 0 (the Go zero value) means no year.
        if (group.year && group.year !== currentYear) {
          // Omit groups from other years.
          return false;
        }
        if (group.membershipHidden) {
          return false;
        }
        if (group.type === GroupType.GROUP) {
          // Omit My Groups which the current user isn't a current staff member in.
          let staff = group.get('currentStaff') ?? [];
          return !!findBy(staff, 'personId', this.session.personId);
        }
        // Include all other groups.
        return true;
      })
      .sort(
        (a, b) =>
          a.type - b.type || (a.label || '').localeCompare(b.label || ''),
      );
  }

  // Written defensively (rather than the setDiff computed macro, and
  // against group_ids rather than event.groups directly) because
  // event.groups is an async hasMany -- calling .includes() on its
  // PromiseManyArray directly is deprecated, and safeIds already handles
  // the case where event is mid-teardown (e.g. cancelling a new, unsaved
  // event calls event.deleteRecord() while this is still on screen).
  @computed('filteredGroups.[]', 'event.group_ids.[]')
  get remainingGroups() {
    let assignedGroupIds = this.safeIds('group_ids');
    return this.filteredGroups.filter(
      (group) => !assignedGroupIds.includes(group.id),
    );
  }

  @computed('remainingGroups')
  get sortedRemainingGroups() {
    let groupTypes = uniq(mapBy(this.remainingGroups, 'type')).sort();
    let sortedRemainingGroups = [];
    groupTypes.forEach((groupType) => {
      let semiFilteredGroups = filterBy(
        this.remainingGroups,
        'type',
        groupType,
      );
      let groupings = uniq(mapBy(semiFilteredGroups, 'grouping')).sort();
      groupings.forEach((grouping) => {
        sortedRemainingGroups.push(
          ...filterBy(semiFilteredGroups, 'grouping', grouping).sort(
            naturalSort('label'),
          ),
        );
      });
    });
    return sortedRemainingGroups;
  }

  @computed('event.staff_ids')
  get sortedRemainingStaff() {
    let staffIds = this.safeIds('staff_ids');
    return this.store
      .peekAll('person')
      .filter((person) => {
        return (
          person.type === PersonType.STAFF && !staffIds.includes(person.id)
        );
      })
      .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
  }

  refreshRisks() {
    // events/risk is fetched via store.query, which returns a PromiseArray
    // -- calling .filter() on it directly (as sortedRemainingRisks does) is
    // deprecated, so resolve it into a plain tracked array first.
    this.store
      .query('events/risk', {
        schoolId: this.session.activeSchool,
      })
      .then((risks) => {
        if (this.isDestroying || this.isDestroyed) {
          return;
        }
        this.risks = risks.slice();
      });
  }

  @computed('event.risk_ids', 'risks.[]')
  get sortedRemainingRisks() {
    let riskIds = this.safeIds('risk_ids');
    return this.risks
      .filter((risk) => {
        return !riskIds.includes(risk.id);
      })
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  @computed('event.permission_ids')
  get sortedRemainingOptions() {
    let permissionIds = this.safeIds('permission_ids');
    return this.store
      .peekAll('events/permission')
      .filter((permission) => permission.type === PermissionType.OPTION)
      .filter((permission) => {
        return !permissionIds.includes(permission.id);
      })
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  @computed('event.permission_ids')
  get sortedRemainingPermissions() {
    let permissionIds = this.safeIds('permission_ids');
    return this.store
      .peekAll('events/permission')
      .filter((permission) => permission.type === PermissionType.PERMISSION)
      .filter((permission) => {
        return !permissionIds.includes(permission.id);
      })
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  @computed('event.permissionField_ids', 'permissionFields.[]')
  get sortedRemainingFieldPermissions() {
    if (!isEmpty(this.permissionFields)) {
      let permissionFieldIds = this.safeIds('permissionField_ids');
      return this.permissionFields
        .filter((permission) => {
          return !permissionFieldIds.includes(permission.id);
        })
        .sort((a, b) => (a.label > b.label ? 1 : a.label < b.label ? -1 : 0));
    }
  }

  // TinyMCE editor
  // editorOptions returns an object for TinyMCE editor config with autosave enabled.
  // We need a unique element ID for specifying selectors and localStorage keys.
  @computed('editorId')
  get editorOptions() {
    return Object.freeze(autosaveTinyMCEConfig(this.editorId));
  }

  @computed('riskEditorId', 'event.riskAcknowledgement')
  get riskEditorOptions() {
    let config = autosaveTinyMCEConfig(this.riskEditorId);
    config.readonly = !this.event.riskAcknowledgement;
    return Object.freeze(config);
  }

  // editorId returns an element ID to assign to the editor's children
  @computed('session.person.id', 'additionalAutosavePrefix')
  get editorId() {
    let userId = this.session.person.get('id');
    return autosaveCreateEditorId(userId, this.additionalAutosavePrefix);
  }

  @computed('session.person.id', 'additionalRiskAutosavePrefix')
  get riskEditorId() {
    let userId = this.session.person.get('id');
    return autosaveCreateEditorId(userId, this.additionalRiskAutosavePrefix);
  }

  // the following prefixes are used in editorId
  // if a parent component or route adds it for more uniqueness.
  @tracked additionalAutosavePrefix = 'event';

  @tracked additionalRiskAutosavePrefix = 'event-risk';

  // This is all gross as but follows the new-post pattern
  // and allows calendar driven event adding/editing
  @observes('dialogOpen')
  _watchOpen() {
    if (this.dialogOpen) {
      this.openDialog();
    }
  }

  openDialog() {
    this.refreshLocations();
    this.refreshTemplates();
    this.refreshRisks();

    // Initialise attachments array
    this.attachments = [];
    this.selectedLocation = null;
    this.selectedTemplate = null;
    if (this.event) {
      let current = this.event;
      // Load the location and the template separately, so that one failed
      // request does not clear the other field. Do not show a loaded record
      // if the user picked another value while it loaded. belongsTo() needs
      // the relationship names: locationModel and template are aliases.
      for (let [key, field] of [
        ['locationId', 'selectedLocation'],
        ['templateId', 'selectedTemplate'],
      ]) {
        current[key]
          .then((record) => {
            if (this.isDestroying || this.isDestroyed) {
              return;
            }
            if (this.event !== current) {
              return;
            }
            if (current.belongsTo(key).value() !== record) {
              return;
            }
            this[field] = record;
          })
          .catch(() => {
            if (this.isDestroying || this.isDestroyed) {
              return;
            }
            this.toaster.error(`Error loading event location or template.`);
          });
      }
      // riskMedia is an async hasMany -- map directly on its PromiseManyArray
      // is deprecated, so resolve it first. This can still be pending when
      // an upload finishes and adds to this.attachments (see uppy's upload
      // callback below), so merge in the loaded records rather than
      // replacing the array outright - otherwise a slow riskMedia load can
      // wipe out an upload that landed first.
      let event = this.event;
      event.riskMedia
        .then((riskMedia) => {
          // The dialog may have been closed, or reopened against another
          // event, while the media was loading.
          if (this.isDestroying || this.isDestroyed) {
            return;
          }
          if (this.event !== event) {
            return;
          }
          let existing = this.attachments;
          let loaded = riskMedia
            .map((record) => ({ record }))
            .filter(
              (attachment) =>
                !findBy(existing, 'record.id', attachment.record.id),
            );
          this.attachments = [...loaded, ...existing];
        })
        .catch(() => {
          if (this.isDestroying || this.isDestroyed) {
            return;
          }
          this.toaster.error(`Error loading event risk media.`);
        });
    }
    this.cancelling = false;
    this.initUppy();

    if (!this.event) {
      let start = moment()
        .add(1, 'day')
        .set({ hours: 9, minutes: 0, seconds: 0 })
        .unix();
      if (this.selectedDate) {
        start = moment(this.selectedDate, 'YYYY-MM-DD').add(9, 'hours').unix();
      }
      let duration = 60;
      this.event = this.store.createRecord('events/event', {
        schoolId: this.get('session.school'),
        authorId: this.get('session.person'),
        start,
        duration,
        type: EventType.OFFSITE_EXCURSION,
      });
      this.event.setDefaults();
    } else {
      this.showCloseAt = !!this.event.closeAt;
      this.refreshLedgerItems();
    }

    this.store
      .query('finance/stripe-connection', {
        isConnected: true,
        isEnabled: true,
      })
      .then((connections) => {
        if (!isEmpty(connections)) {
          this.stripeConnected = true;
        }
      });

    this.currentStep = 0;
  }

  /**
   * saveExistingEvent saves an event that has already been persisted,
   * reconciling its risk media with the attachments the user has added or
   * removed in the dialog.
   */
  async saveExistingEvent() {
    let attachments = this.attachments;

    // Wait for the risk media relationship before diffing it against the
    // attachments - an unresolved relationship iterates as empty, which would
    // look like every existing attachment had been removed.
    let riskMedia = await this.event.riskMedia;

    let mediaToRemove = riskMedia.filter(
      (record) => !findBy(attachments, 'record.id', record.id),
    );
    let newAttachments = attachments.filter(
      (attachment) => attachment.record.isNew,
    );

    await Promise.all([
      ...mediaToRemove.map((record) => record.destroyRecord()),
      ...newAttachments.map((attachment) => attachment.record.save()),
    ]);

    attachments.forEach((attachment) => {
      let record = attachment.record;
      if (!riskMedia.includes(record)) {
        riskMedia.push(record);
      }
    });

    return this.event.save();
  }

  /**
   * saveNewEvent saves an event for the first time, then associates any
   * attachments with it and re-saves, as the attachments need the event's ID.
   */
  async saveNewEvent() {
    await this.event.save();

    let attachments = this.attachments;
    if (!attachments.length) {
      return;
    }

    // Save the attachments first so that we have their IDs.
    await Promise.all(
      attachments.map((attachment) => attachment.record.save()),
    );

    let riskMedia = await this.event.riskMedia;
    attachments.forEach((attachment) => {
      riskMedia.push(attachment.record);
    });

    return this.event.save();
  }

  @computed('event.id')
  get saveAndContinueDialogOpen() {
    return !this.event.id;
  }

  createRiskAssessmentMatrix() {
    const matrixHtml = `
      <div>
        <h3 style="text-align: center">Risk Assessment Matrix</h3>
      </div>
      <div>
        <table class="risk-table">
          <thead>
            <tr>
              <th rowspan="2">LIKELIHOOD (Probability)</th>
              <th colspan="5">CONSEQUENCE (Severity)</th>
            </tr>
            <tr>
              <th>Insignificant<br>1</th>
              <th>Minor<br>2</th>
              <th>Moderate<br>3</th>
              <th>Major<br>4</th>
              <th>Critical<br>5</th>
            </tr>
            <tr>
              <th></th>
              <th>No treatment<br>required.</th>
              <th>Injury/illness<br>requiring first aid<br>treatment only.</th>
              <th>Injury/illness<br>requiring<br>medication or<br>some treatment.</th>
              <th>Life-threatening<br>injury/illness or<br>requiring<br>hospitalisations.</th>
              <th>Death or multiple life-<br>threatening injuries.</th>
            </tr>
          </thead>
          <tbody id="risk-matrix-body">
            <tr>
              <td><strong>Almost<br>certain<br>5</strong><br>Expected to occur in most<br>circumstances.</td>
              <td>MEDIUM<br>5</td>
              <td>HIGH<br>10</td>
              <td>EXTREME<br>15</td>
              <td>EXTREME<br>20</td>
              <td>EXTREME<br>25</td>
            </tr>
            <tr>
              <td><strong>Likely<br>4</strong><br>High probability of<br>occurring in most<br>circumstances.</td>
              <td>MEDIUM<br>4</td>
              <td>MEDIUM<br>8</td>
              <td>HIGH<br>12</td>
              <td>EXTREME<br>16</td>
              <td>EXTREME<br>20</td>
            </tr>
            <tr>
              <td><strong>Possible<br>3</strong><br>Might occur occasionally.</td>
              <td>LOW<br>3</td>
              <td>MEDIUM<br>6</td>
              <td>HIGH<br>9</td>
              <td>HIGH<br>12</td>
              <td>EXTREME<br>15</td>
            </tr>
            <tr>
              <td><strong>Unlikely<br>2</strong><br>Could occur at some time,<br>doubtful.</td>
              <td>LOW<br>2</td>
              <td>MEDIUM<br>4</td>
              <td>MEDIUM<br>6</td>
              <td>MEDIUM<br>8</td>
              <td>HIGH<br>10</td>
            </tr>
            <tr>
              <td><strong>Rare<br>1</strong><br>May occur but only in<br>exceptional circumstances.</td>
              <td>LOW<br>1</td>
              <td>LOW<br>2</td>
              <td>LOW<br>3</td>
              <td>MEDIUM<br>4</td>
              <td>MEDIUM<br>5</td>
            </tr>
          </tbody>
        </table>
      </div>
  `;

    return matrixHtml;
  }

  addToMitigationTable(risk) {
    let riskLabel = risk.get('label');
    let mitigations = risk.get('mitigations');
    let mitigationHtml = '';

    if (!isEmpty(mitigations)) {
      mitigationHtml += '<ul>';
      mitigations.forEach((mitigation) => {
        let label = mitigation.get('label');
        let content = mitigation.get('content');
        if (content) {
          mitigationHtml += `<li><strong>${label}:</strong> ${content}</li>`;
        } else {
          mitigationHtml += `<li><strong>${label}</strong></li>`;
        }
      });
      mitigationHtml += '</ul>';
    } else {
      mitigationHtml = '<p>No controls defined</p>';
    }

    let newRow = `
  <tr id="${risk.get('id')}">
    <td>${riskLabel}</td>
    <td>${mitigationHtml}</td>
    <td></td>
    <td></td>
  </tr>
`;

    let currentTable = this.event.get('riskMitigations') || '';

    // Check if table exists and has body, otherwise build a new table with the row added
    if (currentTable.includes('<tbody id="risk-mitigation-body">')) {
      let regex = /(<tbody id="risk-mitigation-body">[^]*?)(<\/tbody>)/i;
      let updatedTable = currentTable.replace(regex, `$1${newRow}$2`);
      this.event.set('riskMitigations', updatedTable);
    } else {
      let riskAssessmentMatrix = this.createRiskAssessmentMatrix();

      let riskMitigations = `
      <div class="layout-column">
        <div>
          <table class="risk-table">
            <thead>
              <tr>
                <th>Risk</th>
                <th>Controls</th>
                <th class="grade-header" title="Risk grade before implementing controls">
                    Inherent Risk Grade
                </th>
                <th class="grade-header" title="Risk grade after implementing controls">
                    Residual Risk Grade
                </th>
              </tr>
            </thead>
            <tbody id="risk-mitigation-body">
              ${newRow}
            </tbody>
          </table>
        </div>
        <br>
        ${riskAssessmentMatrix}
      </div>
      `;

      this.event.set('riskMitigations', riskMitigations);
    }
  }

  removeFromMitigationTable(risk) {
    let currentTable = this.event.get('riskMitigations') || '';
    let riskId = risk.get('id');

    if (currentTable.includes(`id="${riskId}"`)) {
      // Create a regex to match the entire row with this risk ID
      let rowRegex = new RegExp(`<tr id="${riskId}"[^>]*>.*?</tr>`, 'gis');

      let updatedTable = currentTable.replace(rowRegex, '');

      // Check if there are any remaining rows in the body
      let tbodyContent = updatedTable.match(
        /<tbody id="risk-mitigation-body">(.*?)<\/tbody>/is,
      );
      if (tbodyContent && tbodyContent[1].trim() === '') {
        let riskAssessmentMatrix = this.createRiskAssessmentMatrix();
        this.event.set(
          'riskMitigations',
          '<p>No risks defined</p>' + riskAssessmentMatrix,
        );
      } else {
        this.event.set('riskMitigations', updatedTable);
      }
    }
  }

  setIntentionToAttendLedgerItem(costMappingOption, ledgerItem) {
    costMappingOption.set('ledgerItemId', ledgerItem.get('id'));
    if (!costMappingOption.amount) {
      costMappingOption.set('amount', ledgerItem.get('defaultAmount'));
    }
    if (!costMappingOption.description) {
      costMappingOption.set(
        'description',
        ledgerItem.get('defaultDescription'),
      );
    }
  }

  @action
  setCostMappingOptionProperty(option, selection, propertyName, property) {
    let costMapping;
    if (this.event.optionCostMappings) {
      costMapping = findBy(
        this.event.optionCostMappings,
        'optionId',
        option.id,
      );
    }
    if (!costMapping) {
      costMapping = this.store.createFragment('events/cost-mapping', {
        optionId: option.id,
      });
      this.event.optionCostMappings.addFragment(costMapping);
    }
    let costMappingOption = findBy(
      costMapping.costMappingOptions,
      'value',
      selection ? selection.value : 'true',
    );
    if (!costMappingOption) {
      costMappingOption = this.store.createFragment(
        'events/cost-mapping-option',
        { value: selection ? selection.value : 'true' },
      );
      costMapping.costMappingOptions.addFragment(costMappingOption);
    }
    switch (propertyName) {
      case 'ledgerItem':
        costMappingOption.set('ledgerItemId', property.get('id'));
        if (!costMappingOption.amount) {
          costMappingOption.set('amount', property.get('defaultAmount'));
        }
        if (!costMappingOption.description) {
          costMappingOption.set('description', selection.get('label'));
        }
        this.refreshLedgerItems();
        break;
      case 'amount':
        if (property >= 0) {
          costMappingOption.set('amount', property);
        }
        break;
      default:
        costMappingOption.set(propertyName, property);
    }
  }

  @action
  addIntentionToAttendCostMappingOption() {
    let costMappingOption = this.store.createFragment(
      'events/cost-mapping-option',
      {},
    );
    this.event.intentionToAttendCostMappingOptions.addFragment(
      costMappingOption,
    );
  }

  // Drop any language violations recorded against an option's cost mappings.
  // Called when the option no longer has a visible cost-mapping row (either
  // because "Incurs Additional Cost" was toggled off, or the option itself
  // was removed), so the orphaned violations don't keep the save button
  // disabled when the user can no longer see the offending field.
  clearViolationsForPermission(permission) {
    let costMapping = findBy(
      this.event.optionCostMappings,
      'optionId',
      permission.id,
    );
    if (!costMapping) return;
    let toRemove = costMapping.costMappingOptions;
    this.descriptionViolatingOptions = this.descriptionViolatingOptions.filter(
      (option) => !toRemove.includes(option),
    );
    this.noteViolatingOptions = this.noteViolatingOptions.filter(
      (option) => !toRemove.includes(option),
    );
  }

  // safeIds reads a computed `_ids` property (e.g. staff_ids, risk_ids) off
  // `event`. Those walk an async hasMany's PromiseManyArray, which can
  // still throw if the relationship itself is touched mid-teardown --
  // e.g. cancelling a new, unsaved event calls event.deleteRecord() while
  // this is still on screen. Falls back to [] rather than crashing the
  // render.
  safeIds(key) {
    try {
      return (this.event && this.event[key]) || [];
    } catch {
      return [];
    }
  }

  refreshLedgerItems() {
    this.store
      .query('finance/ledger-item', {
        eventId: this.event.id,
      })
      .then((ledgerItems) => {
        this.ledgerItems = ledgerItems;
        this.refreshLedgerItemsInUse();
      });
  }

  refreshLedgerItemsInUse() {
    let ledgerItems = this.event.intentionToAttendCostMappingOptions.map(
      (costMappingOption) => {
        return costMappingOption.ledgerItem;
      },
    );
    this.event.optionCostMappings.forEach((optionCostMapping) => {
      optionCostMapping.costMappingOptions.forEach((costMappingOption) => {
        ledgerItems.push(costMappingOption.ledgerItem);
      });
    });
    this.ledgerItemsInUse = ledgerItems.filter((item) => item != null);
  }

  @action
  onFirstStepChange(step) {
    if (this.event.hasPayments) {
      // If we are doing Payments,
      // Add a default Intention To Attend Cost Mapping Option
      if (!this.event.intentionToAttendCostMappingOptions.length) {
        this.addIntentionToAttendCostMappingOption();
      }
    } else {
      // Otherwise tidy up any we may have created stepping back and forth
      this.event.intentionToAttendCostMappingOptions.forEach(
        (costMappingOption) => {
          this.event.intentionToAttendCostMappingOptions.removeFragment(
            costMappingOption,
          );
        },
      );
      this.descriptionViolatingOptions = [];
      this.noteViolatingOptions = [];
    }
    this.currentStep = step;
  }

  @action
  setEventType(type) {
    this.event.set('type', type);
    this.event.setDefaults();
  }

  @action
  setInternal(check) {
    this.event.set('internal', !check);
    if (!check) {
      this.event.setProperties({
        hasPermissions: false,
        hasOptions: false,
        requiresApproval: false,
      });
    }
  }

  @action
  setHasPermissions(check) {
    this.event.set('hasPermissions', check);
    if (check) {
      this.event.set('internal', false);
    }
  }

  @action
  setHasOptions(check) {
    this.event.set('hasOptions', check);
    if (check) {
      this.event.set('internal', false);
    }
  }

  @action
  setRequiresApproval(check) {
    this.event.set('requiresApproval', check);
    if (check) {
      this.event.set('internal', false);
    }
  }

  @action
  setHasPayments(check) {
    this.event.set('hasPayments', check);
    if (!check) {
      // Clear any language violations recorded against payment cost mappings
      // so the Save button reflects the absence of the payments step.
      this.descriptionViolatingOptions = [];
      this.noteViolatingOptions = [];
    }
  }

  @action
  handleShowCloseAt(check) {
    this.showCloseAt = check;
    if (!check) {
      this.event.set('closeAt', undefined);
    }
  }

  @action
  cancel() {
    if (!this.event.id) {
      this.event.deleteRecord();
      this.event = null;
    } else {
      // Restore any altered attributes and relationships.
      rollbackRecord(this.event);
    }
    this.setProperties({
      dialogOpen: false,
      sortingPermissions: false,
      descriptionViolatingOptions: [],
      noteViolatingOptions: [],
    });
  }

  @action
  onDescriptionViolationChange(costMappingOption, violations) {
    if (!costMappingOption) return;
    if (isEmpty(violations)) {
      this.descriptionViolatingOptions =
        this.descriptionViolatingOptions.filter(
          (option) => option !== costMappingOption,
        );
    } else if (!this.descriptionViolatingOptions.includes(costMappingOption)) {
      this.descriptionViolatingOptions = [
        ...this.descriptionViolatingOptions,
        costMappingOption,
      ];
    }
  }

  @action
  onNoteViolationChange(costMappingOption, violations) {
    if (!costMappingOption) return;
    if (isEmpty(violations)) {
      this.noteViolatingOptions = this.noteViolatingOptions.filter(
        (option) => option !== costMappingOption,
      );
    } else if (!this.noteViolatingOptions.includes(costMappingOption)) {
      this.noteViolatingOptions = [
        ...this.noteViolatingOptions,
        costMappingOption,
      ];
    }
  }

  @action
  removeIntentionToAttendCostMappingOption(option) {
    this.event.intentionToAttendCostMappingOptions.removeFragment(option);
    this.descriptionViolatingOptions = this.descriptionViolatingOptions.filter(
      (item) => item !== option,
    );
    this.noteViolatingOptions = this.noteViolatingOptions.filter(
      (item) => item !== option,
    );
  }

  @action
  setIntentionToAttendCostMappingAmount(costMappingOption, amount) {
    if (amount >= 0) {
      costMappingOption.set('amount', amount);
    }
  }

  @action
  addLedgerItem(ledgerItem, option, selection) {
    this.refreshLedgerItems();
    this.setCostMappingOptionProperty(
      option,
      selection,
      'ledgerItem',
      ledgerItem,
    );
  }

  @action
  addIntentionToAttendLedgerItem(costMappingOption, ledgerItem) {
    this.refreshLedgerItems();
    this.setIntentionToAttendLedgerItem(costMappingOption, ledgerItem);
  }

  @action
  removeLedgerItem(costMappingOption) {
    costMappingOption.set('ledgerItemId', null);
    costMappingOption.set('amount', null);
    costMappingOption.set('description', null);
    this.refreshLedgerItems();
  }

  /**
   * Save the event to LincEd.
   */
  @action
  async saveEvent(carryOn) {
    this.saveInProgress = true;

    // set the jurisdiction
    this.event.set('jurisdiction', this.jurisdiction);

    if (!isEmpty(this.event.locationModel.get('id'))) {
      this.event.set('location', this.event.locationModel.get('name'));
    }

    if (this.event.id) {
      try {
        await this.saveExistingEvent();

        this.setProperties({
          dialogOpen: carryOn,
          saveInProgress: false,
          sortingFieldPermissions: false,
          sortingOptions: false,
          sortingPermissions: false,
        });
        if (this.calendarMode) {
          this.event = null;
        }
        // Remove autosaves from the local storage
        let path = window.location.pathname;
        removeAutosavedDrafts(path, this.editorId);
        removeAutosavedDrafts(path, this.riskEditorId);

        this.toaster.success(`Event updated successfully.`);
      } catch {
        this.toaster.error(`Error saving Event!`);
      }
    } else {
      try {
        await this.saveNewEvent();

        this.refresh();
        this.setProperties({
          dialogOpen: carryOn,
          saveInProgress: false,
          sortingFieldPermissions: false,
          sortingOptions: false,
          sortingPermissions: false,
        });
        if (carryOn) {
          this.currentStep = this.currentStep - 1;
        } else {
          this.event = null;
        }
        // Remove autosaves from the local storage
        let path = window.location.pathname;
        removeAutosavedDrafts(path, this.editorId);
        removeAutosavedDrafts(path, this.riskEditorId);
        this.toaster.success('Event saved!');
      } catch {
        this.saveInProgress = false;
        this.toaster.error('Error saving Event!');
      }
    }
  }

  @action
  addImages() {
    this.uppy.addImages();
  }

  @action
  addVideos() {
    this.uppy.addVideos();
  }

  @action
  addFiles() {
    this.uppy.addFiles();
  }

  @action
  removeAttachment(attachment) {
    this.attachments = this.attachments.filter((a) => a !== attachment);
  }

  /**
   * Launch's the Google Drive picker...
   * @param {*} postImmediately - if set to true, then automatically post to the feed once complete.
   */
  @action
  launchGooglePicker(postImmediately = false) {
    window.launchPickerCallback = this;

    this.postImmediately = postImmediately;
    this.googleDrive
      .pickFiles()
      .then((files) => this.embedDriveFiles(files))
      .catch((err) => this.googleDrive.showEmbedError(err));
  }

  @action
  closeGoogleDrivePermissionDialog(action) {
    if (typeOf(action) === 'function') {
      action();
    }
    this.googleDrivePermissionDialogOpen = false;
  }

  // this.event.<relation> hasMany relations resolve to a PromiseManyArray,
  // which doesn't have push/indexOf/splice until the promise settles -- so
  // every mutation has to go through the resolved ManyArray instead.
  addToRelationship(relationship, ...records) {
    relationship.then((resolved) => {
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      resolved.push(...records);
    });
  }

  removeFromRelationship(relationship, record) {
    relationship.then((resolved) => {
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      let index = resolved.indexOf(record);
      if (index !== -1) {
        resolved.splice(index, 1);
      }
    });
  }

  // Groups
  @action
  addGroup(group) {
    this.addToRelationship(this.event.groups, group);
    this.addToRelationship(
      this.event.staff,
      ...group.get('currentStaffPeople'),
    );
  }

  @action
  removeGroup(group) {
    this.removeFromRelationship(this.event.groups, group);
  }

  // Staff
  @action
  addStaff(staff) {
    this.addToRelationship(this.event.staff, staff);
  }

  @action
  removeStaff(staff) {
    this.removeFromRelationship(this.event.staff, staff);
  }

  @action
  addRisk(risk) {
    this.addToRelationship(this.event.risks, risk);
    this.addToMitigationTable(risk);
  }

  @action
  removeRisk(risk) {
    this.removeFromRelationship(this.event.risks, risk);
    this.removeFromMitigationTable(risk);
  }

  // Permissions
  @action
  addPermission(permission) {
    this.addToRelationship(this.event.permissions, permission);
    // Default permission permissions to mandatory
    // Except switches because that makes no sense
    if (
      permission.type === PermissionType.PERMISSION &&
      permission.inputType !== InputType.SWITCH
    ) {
      this.addToRelationship(this.event.mandatoryPermissions, permission);
    }
  }

  @action
  removePermission(permission) {
    // Clear violations before mutating state so the lookup inside
    // clearViolationsForPermission can't miss a torn-down cost mapping.
    this.clearViolationsForPermission(permission);
    this.removeFromRelationship(this.event.permissions, permission);
    this.removeFromRelationship(this.event.mandatoryPermissions, permission);
    this.removeFromRelationship(
      this.event.costIncurringPermissions,
      permission,
    );
  }

  @action
  addFieldPermission(permission) {
    this.addToRelationship(this.event.permissionFields, permission);
  }

  @action
  removeFieldPermission(permission) {
    this.removeFromRelationship(this.event.permissionFields, permission);
  }

  @action
  togglePermissionMandatory(permission, value) {
    if (value) {
      this.addToRelationship(this.event.mandatoryPermissions, permission);
    } else {
      this.removeFromRelationship(this.event.mandatoryPermissions, permission);
    }
  }

  @action
  togglePermissionFieldMandatory(permission, value) {
    if (value) {
      this.addToRelationship(this.event.mandatoryPermissionFields, permission);
    } else {
      this.removeFromRelationship(
        this.event.mandatoryPermissionFields,
        permission,
      );
    }
  }

  @action
  togglePermissionCosts(permission, value) {
    if (value) {
      this.addToRelationship(this.event.costIncurringPermissions, permission);
    } else {
      // Clear violations before mutating state so the lookup inside
      // clearViolationsForPermission can't miss a torn-down cost mapping.
      this.clearViolationsForPermission(permission);
      this.removeFromRelationship(
        this.event.costIncurringPermissions,
        permission,
      );
    }
  }

  @action
  clearEventCloseAt() {
    this.set('event.closeAt', undefined);
  }

  @action
  setEventStartDate(newDate) {
    this.set('event.start', moment(newDate[0]).unix());
  }

  @action
  setEventEndDate(newDate) {
    this.set(
      'event.duration',
      (moment(newDate[0]).unix() - this.event.start) / 60,
    );
  }

  @action
  setEventCloseAt(newDate) {
    this.set('event.closeAt', moment(newDate[0]).unix());
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    // A fragment array's .splice() comes from the fragment-array-mutable
    // initializer (StatefulArray only has .replace() natively).
    const [item] = sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);
  }

  @action
  selectLocation(location) {
    this.selectedLocation = location;
    this.event.set('locationModel', location);
  }

  @action
  addTemplate(template) {
    this.selectedTemplate = template;
    if (isEmpty(template)) {
      this.event.setProperties({
        template: null,
        description: '',
      });
      return;
    }
    let description = template.content
      .replace(/{EVENT_NAME}/g, this.event.get('title'))
      .replace(/{TRANSPORT}/g, this.event.get('transport'));
    this.event.setProperties({
      template,
      description,
    });
  }

  @action
  startUpload() {
    this.uploading = true;
    this.empty = false;
  }

  @action
  handleUpload(file, upload) {
    let { key, mediaSource } = getKeyAndSourceFromUpload(upload);
    let event = this.event;

    let sharedWith = SharingLevel.SCHOOL;

    this.store
      .createRecord('media', {
        schoolId: this.get('session.school'),
        personId: this.get('session.person'),
        sharedWith,
        mediaType: MediaType.IMAGE,
        mediaSource: mediaSource,
        mimeType: file.type,
        fileName: file.name,
        storageKey: key,
        fileSize: file.size,
      })
      .save()
      .then((media) => {
        event.set('eventImage', media.id);
      })
      .catch(() => {
        event.rollbackAttributes();
        this.toaster.error(`Error saving image!`);
      })
      .finally(() => {
        this.uploading = false;
        this.hideImage = false;
        this.empty = true;
      });
  }

  /**
   * handleUploadError provides the callback to deal with upload errors.
   */
  @action
  handleUploadError() {
    this.uploading = false;
    this.toaster.error(`Error saving event image!`);
  }

  @action
  revertImage() {
    this.event.set('eventImage', '');
    this.hideImage = false;
  }

  didInsertElement() {
    super.didInsertElement(...arguments);
    this.initUppy();
    // TinyMCE autosave plugin
    // Manage the button's state on initial insertion
    manageAutosaveDraftsButton(this.editorId);
    manageAutosaveDraftsButton(this.riskEditorId);
  }

  willUpdate() {
    super.willUpdate(...arguments);
    // TinyMCE autosave plugin
    // Manage the button's state on initial update
    manageAutosaveDraftsButton(this.editorId);
    manageAutosaveDraftsButton(this.riskEditorId);
  }

  didUpdate() {
    super.didUpdate(...arguments);

    // Cancel any pending timer.
    if (!isEmpty(this.timer)) {
      cancel(this.timer);
    }
    this.timer = later(
      this,
      function () {
        manageAutosaveDraftsButton(this.editorId);
        manageAutosaveDraftsButton(this.riskEditorId);
      },
      3000,
    );
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.hideImage = false;
    // Cancel any pending timer.
    if (!isEmpty(this.timer)) {
      cancel(this.timer);
    }
  }

  @tracked
  googleDrivePermissionDialogOpen = false;
  @tracked
  googleDrivePermissionStatus = null;
  @tracked
  googleDrivePermissionResolve = null;
  @tracked
  googleDrivePermissionReject = null;

  @tracked
  event;

  @tracked
  postImmediately;

  @tracked
  timer;

  // embedDriveFiles adds the picked Google Drive files as attachments.
  embedDriveFiles(files) {
    for (const file of files) {
      this.googleDrive
        .embedFile(file, (status) => {
          // The user can still embed the file in the permission dialog.
          return new Promise((resolve, reject) => {
            this.googleDrivePermissionDialogOpen = true;
            this.googleDrivePermissionStatus = status;
            this.googleDrivePermissionResolve = resolve;
            // Closing the dialog is a cancel, which needs no message.
            this.googleDrivePermissionReject = () =>
              reject({ userCancelled: true });
          });
        })
        .then((media) => {
          if (!media) {
            return;
          }

          let record = this.store.createRecord('media', {
            ...media,
            schoolId: this.computedSchool,
            personId: this.get('session.person'),
            sharedWith: SharingLevel.SCHOOL,
          });

          this.attachments = [...this.attachments, { record }];
        })
        .catch((err) => this.googleDrive.showEmbedError(err, file));
    }
  }

  initUppy() {
    this.uppy.setupAttachments((file, upload, mediaType) => {
      let attachment = onAttachmentUpload(
        this.store,
        SharingLevel.SCHOOL,
        this.get('session.school'),
        this.get('session.person'),
        file,
        upload,
        mediaType,
      );
      this.attachments = [...this.attachments, attachment];
    });
  }
}
